import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/layout/MainLayout';
import { useAuth } from '../context/AuthContext';
import { getBackofficeSidebarItems } from '../features/backoffice/shell/backofficeSidebar';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type Department = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  heroTitle?: string | null;
  heroSubtitle?: string | null;
  heroImageUrl?: string | null;
  productUnitLabels?: string[];
  sortOrder: number;
  isActive: boolean;
};

type StaffDepartmentAccess = {
  userId: string;
  email: string;
  type: 'EMPLOYEE' | 'SHIPPING_AGENT';
  fullName?: string | null;
  departmentIds: string[];
};

type DepartmentFormState = {
  name: string;
  slug: string;
  description: string;
  heroTitle: string;
  heroSubtitle: string;
  productUnitLabelsText: string;
  sortOrder: string;
  isActive: boolean;
};

const defaultFormState: DepartmentFormState = {
  name: '',
  slug: '',
  description: '',
  heroTitle: '',
  heroSubtitle: '',
  productUnitLabelsText: '',
  sortOrder: '0',
  isActive: true,
};

function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function DepartmentManagementPage() {
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const location = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [departments, setDepartments] = React.useState<Department[]>([]);
  const [form, setForm] = React.useState<DepartmentFormState>(defaultFormState);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [heroImageFile, setHeroImageFile] = React.useState<File | null>(null);
  const [existingHeroImageUrl, setExistingHeroImageUrl] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [accessRows, setAccessRows] = React.useState<StaffDepartmentAccess[]>([]);
  const [accessSavingUserId, setAccessSavingUserId] = React.useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  const loadDepartments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await globalAuthorizedFetch(`${API_BASE}/departments/workspace`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Unable to load departments.');
      }
      const data = (await response.json()) as Department[];
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load departments.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    void loadDepartments();
  }, [loadDepartments]);

  const loadDepartmentAccess = React.useCallback(async () => {
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/departments/workspace/access-users`);
      if (!res.ok) return;
      const data = (await res.json()) as StaffDepartmentAccess[];
      setAccessRows(Array.isArray(data) ? data : []);
    } catch {
      // ignore
    }
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    void loadDepartmentAccess();
  }, [loadDepartmentAccess]);

  const resetForm = React.useCallback(() => {
    setForm(defaultFormState);
    setEditingId(null);
    setHeroImageFile(null);
    setExistingHeroImageUrl('');
    setShowCreateForm(false);
  }, []);

  const toFullImageUrl = React.useCallback((raw: string | null | undefined) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    if (value.startsWith('/uploads/')) return `${API_BASE}${value}`;
    return value;
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const cleanedName = form.name.trim();
      if (!cleanedName) {
        throw new Error('Department name is required.');
      }
      const computedSlug = normalizeSlug(form.slug || cleanedName);
      if (!computedSlug) {
        throw new Error('Please provide a valid slug.');
      }
      let heroImageUrl = existingHeroImageUrl || null;
      if (heroImageFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', heroImageFile);
        const uploadRes = await globalAuthorizedFetch(`${API_BASE}/departments/workspace/image`, {
          method: 'POST',
          body: uploadForm,
        });
        if (!uploadRes.ok) {
          throw new Error('Unable to upload hero image.');
        }
        const uploadData = await uploadRes.json();
        heroImageUrl = uploadData?.url || null;
      }

      const payload = {
        name: cleanedName,
        slug: computedSlug,
        description: form.description.trim() || null,
        heroTitle: form.heroTitle.trim() || null,
        heroSubtitle: form.heroSubtitle.trim() || null,
        heroImageUrl,
        productUnitLabels: Array.from(
          new Set(
            form.productUnitLabelsText
              .split(/[\n,]/g)
              .map((x) => x.trim().toLowerCase())
              .filter(Boolean),
          ),
        ),
        sortOrder: Number(form.sortOrder || 0),
        isActive: form.isActive,
      };

      const endpoint = editingId
        ? `${API_BASE}/departments/workspace/${editingId}`
        : `${API_BASE}/departments/workspace`;
      const method = editingId ? 'PATCH' : 'POST';
      const response = await globalAuthorizedFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const msg = Array.isArray(payload?.message)
          ? payload.message[0]
          : payload?.message;
        throw new Error(msg || (editingId ? 'Unable to update department.' : 'Unable to create department.'));
      }
      resetForm();
      setSuccess(editingId ? 'Department updated successfully.' : 'Department created successfully.');
      await loadDepartments();
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : (editingId ? 'Unable to update department.' : 'Unable to create department.');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (dep: Department) => {
    setEditingId(dep.id);
    setShowCreateForm(true);
    setHeroImageFile(null);
    setExistingHeroImageUrl(dep.heroImageUrl || '');
    setForm({
      name: dep.name,
      slug: dep.slug,
      description: dep.description || '',
      heroTitle: dep.heroTitle || '',
      heroSubtitle: dep.heroSubtitle || '',
      productUnitLabelsText: Array.isArray(dep.productUnitLabels)
        ? dep.productUnitLabels.join(', ')
        : '',
      sortOrder: String(dep.sortOrder ?? 0),
      isActive: Boolean(dep.isActive),
    });
    setSuccess(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleUserDepartment = (
    userId: string,
    departmentId: string,
    checked: boolean,
  ) => {
    setAccessRows((prev) => prev.map((row) => {
      if (row.userId !== userId) return row;
      const next = new Set(row.departmentIds);
      if (checked) {
        next.add(departmentId);
      } else {
        next.delete(departmentId);
      }
      return { ...row, departmentIds: Array.from(next) };
    }));
  };

  const saveUserDepartmentAccess = async (row: StaffDepartmentAccess) => {
    setAccessSavingUserId(row.userId);
    setError(null);
    setSuccess(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/departments/workspace/access-users/${row.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          departmentIds: row.departmentIds,
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.message || 'Unable to save department access');
      }
      setSuccess(`Department access saved for ${row.fullName || row.email}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save department access');
    } finally {
      setAccessSavingUserId(null);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      forceWorkspaceChrome
      activePath={location.pathname}
    >
      <section className="space-y-6">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Admin
          </p>
          <h1 className="text-3xl font-bold text-slate-900">Department Management</h1>
          <p className="max-w-3xl text-sm text-slate-600">
            Create and manage storefront departments (stores) like Grocery, Dress Shop, or
            Appliances. Every department gets its own URL context and scoped catalog data.
          </p>
        </header>

        {(editingId || showCreateForm) ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">
                {editingId ? 'Edit Department' : 'Create Department'}
              </h2>
            </div>
          <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Name *
              <input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
                placeholder="Grocery"
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Slug
              <input
                value={form.slug}
                onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
                placeholder="grocery"
              />
            </label>
            <label className="sm:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Description
              <textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                className="min-h-[88px] rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
                placeholder="Short description shown on the departments landing page."
              />
            </label>
            <label className="sm:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Allowed Unit Labels
              <textarea
                value={form.productUnitLabelsText}
                onChange={(e) => setForm((prev) => ({ ...prev, productUnitLabelsText: e.target.value }))}
                className="min-h-[72px] rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
                placeholder="lb, kg, pack, carton, pair"
              />
              <span className="text-xs text-slate-500">
                Comma or new-line separated. Product save is enforced against this list for this department.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Hero Title
              <input
                value={form.heroTitle}
                onChange={(e) => setForm((prev) => ({ ...prev, heroTitle: e.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Hero Subtitle
              <input
                value={form.heroSubtitle}
                onChange={(e) => setForm((prev) => ({ ...prev, heroSubtitle: e.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
              />
            </label>
            <label className="sm:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
              Hero Image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setHeroImageFile(file);
                }}
                className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-slate-50 hover:file:bg-slate-700"
              />
              {existingHeroImageUrl ? (
                <img
                  src={toFullImageUrl(existingHeroImageUrl)}
                  alt="Current department hero"
                  className="mt-2 h-28 w-full max-w-sm rounded-lg border border-slate-200 object-cover"
                />
              ) : null}
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              Sort Order
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
                className="rounded-xl border border-slate-300 px-3 py-2 outline-none ring-indigo-200 focus:ring"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              Active
            </label>

            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (editingId ? 'Saving...' : 'Creating...') : (editingId ? 'Save Changes' : 'Create Department')}
              </button>
              {editingId ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel Edit
                </button>
              ) : showCreateForm ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              ) : null}
              {success ? <span className="text-sm text-emerald-700">{success}</span> : null}
            </div>
          </form>
            {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
          </div>
        ) : null}

        {!editingId ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900">Existing Departments</h2>
              {!showCreateForm ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(true);
                    setForm(defaultFormState);
                    setHeroImageFile(null);
                    setExistingHeroImageUrl('');
                    setError(null);
                    setSuccess(null);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  New department
                </button>
              ) : (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Hide form
                </button>
              )}
            </div>
          {loading ? (
            <p className="mt-3 text-sm text-slate-500">Loading departments...</p>
          ) : departments.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No departments found.</p>
          ) : (
            <div className="mt-4 grid gap-3">
              {departments.map((dep) => (
                <article
                  key={dep.id}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-base font-semibold text-slate-900">{dep.name}</h3>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        dep.isActive
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {dep.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">Slug: {dep.slug}</p>
                  {Array.isArray(dep.productUnitLabels) && dep.productUnitLabels.length > 0 ? (
                    <p className="mt-1 text-sm text-slate-600">
                      Units: {dep.productUnitLabels.join(', ')}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500">Units: no restriction configured</p>
                  )}
                  {dep.description ? (
                    <p className="mt-2 text-sm text-slate-700">{dep.description}</p>
                  ) : null}
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(dep)}
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
          </div>
        ) : null}

        {!editingId ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-base font-semibold text-slate-900">
            Staff Department Access
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Assign one or more departments per staff user. Non-admin staff can only access assigned departments.
          </p>
          <div className="mt-4 space-y-3">
            {accessRows.length === 0 ? (
              <p className="text-sm text-slate-500">No staff users found.</p>
            ) : (
              accessRows.map((row) => (
                <article key={row.userId} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {row.fullName || row.email}
                      </p>
                      <p className="text-xs text-slate-600">{row.email} · {row.type}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => saveUserDepartmentAccess(row)}
                      disabled={accessSavingUserId === row.userId}
                      className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                    >
                      {accessSavingUserId === row.userId ? 'Saving...' : 'Save access'}
                    </button>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {departments.map((dep) => {
                      const checked = row.departmentIds.includes(dep.id);
                      return (
                        <label key={`${row.userId}:${dep.id}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => toggleUserDepartment(row.userId, dep.id, e.target.checked)}
                          />
                          {dep.name}
                        </label>
                      );
                    })}
                  </div>
                </article>
              ))
            )}
          </div>
          </div>
        ) : null}
      </section>
    </MainLayout>
  );
}
